import { getRecordValue } from 'src/modules/shared/utils/record.util';
import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React from 'react';
import type { TCAction } from 'src/api/generated';
import type { WorkflowDefinitionDraft } from 'src/modules/tools/utils/workflow-definition.type';

/** Use Workflow Editor Draft Params contract. */
interface UseWorkflowEditorDraftParams<
  T extends WorkflowDefinitionDraft | undefined,
> {
  /** Current editable workflow definition. */
  definitionDraft: T;
  /** Updates the editable workflow definition. */
  setDefinitionDraft: React.Dispatch<React.SetStateAction<T>>;
  /** Updates the selected workflow step. */
  setSelectedStepId: React.Dispatch<React.SetStateAction<string | undefined>>;
  /** Available actions that can be added as workflow steps. */
  actions: TCAction[];
  /** Current action picker search query. */
  actionSearch: string;
  /** Updates the action picker search query. */
  setActionSearch: React.Dispatch<React.SetStateAction<string>>;
  /** Controls visibility of the action picker. */
  setIsAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Identifier of the canvas start node. */
  startNodeId: string;
  /** Identifier of the canvas end node. */
  endNodeId: string;
}

/** Use Workflow Editor Draft Result contract. */
interface UseWorkflowEditorDraftResult {
  /** Whether the action picker is adding a final step. */
  pendingFinal: boolean;
  /** Actions matching the current picker search. */
  filteredActions: TCAction[];
  /** Identifiers of steps reachable from the initial step. */
  reachableSteps: Set<string>;
  /** Whether the draft contains regular or final steps. */
  hasSteps: boolean;
  /** Whether the workflow has an initial step. */
  hasInitialStep: boolean;
  /** Opens the action picker for a regular step. */
  handleRequestAddStep: (sourceId: string) => void;
  /** Opens the action picker for the final step. */
  handleRequestAddFinal: () => void;
  /** Adds the selected action to the workflow draft. */
  handleSelectAction: (action: TCAction) => void;
  /** Closes the action picker and resets pending selection. */
  closeActionPicker: () => void;
  /** Adds a connection to the workflow draft. */
  handleAddConnection: (sourceId: string, targetId: string) => void;
  /** Removes a connection from the workflow draft. */
  handleRemoveConnection: (sourceId: string, targetId: string) => void;
  /** Updates inputs for the specified workflow step. */
  handleChangeStepInputs: (
    stepId: string,
    inputs: Record<string, unknown>
  ) => void;
  /** Deletes a step and its references from the draft. */
  handleDeleteStep: (stepId: string) => void;
}

export const useWorkflowEditorDraft = <
  T extends WorkflowDefinitionDraft | undefined,
>({
  definitionDraft,
  setDefinitionDraft,
  setSelectedStepId,
  actions,
  actionSearch,
  setActionSearch,
  setIsAddModalOpen,
  startNodeId,
  endNodeId,
}: UseWorkflowEditorDraftParams<T>): UseWorkflowEditorDraftResult => {
  const [pendingSourceId, setPendingSourceId] = React.useState<string | null>(
    null
  );
  const [pendingFinal, setPendingFinal] = React.useState(false);

  const generateStepId = React.useCallback(
    (actionName: string) => {
      const baseRaw =
        (nonEmptyString(actionName.split('.').pop()) ?? actionName) || 'paso';
      const base =
        baseRaw
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'paso';
      const existingIds = new Set<string>();
      const steps = definitionDraft?.steps ?? {};
      Object.keys(steps).forEach((id) => existingIds.add(id));
      if (definitionDraft?.finalStep?.id) {
        existingIds.add(definitionDraft.finalStep.id);
      }
      let candidate = base;
      let counter = 1;
      while (existingIds.has(candidate)) {
        candidate = `${base}-${counter}`;
        counter += 1;
      }
      return candidate;
    },
    [definitionDraft]
  );

  const handleRequestAddStep = React.useCallback(
    (sourceId: string) => {
      setPendingFinal(false);
      setPendingSourceId(sourceId);
      setIsAddModalOpen(true);
    },
    [setIsAddModalOpen]
  );

  const handleRequestAddFinal = React.useCallback(() => {
    setPendingFinal(true);
    setPendingSourceId(null);
    setIsAddModalOpen(true);
  }, [setIsAddModalOpen]);

  const closeActionPicker = React.useCallback(() => {
    setIsAddModalOpen(false);
    setPendingSourceId(null);
    setPendingFinal(false);
    setActionSearch('');
  }, [setActionSearch, setIsAddModalOpen]);

  const handleSelectAction = React.useCallback(
    (action: TCAction) => {
      const newId = generateStepId(
        nonEmptyString(action.name || action.annotations?.title) ?? 'paso'
      );
      const actionId =
        nonEmptyString(action.name || action.annotations?.title) ?? 'accion';
      setDefinitionDraft((current) => {
        if (!current) return current;

        if (pendingFinal) {
          if (current.finalStep) return current;
          return {
            ...current,
            finalStep: {
              id: newId,
              actionId,
              inputs: {},
              description: action.description,
            },
          };
        }

        const steps = current.steps ?? {};
        const newStep = {
          id: newId,
          actionId,
          inputs: {},
          nextStepIds: [] as string[],
          description: action.description,
        };

        const updatedSteps = {
          ...steps,
          [newId]: newStep,
        };

        let updated: WorkflowDefinitionDraft = {
          ...current,
          steps: updatedSteps,
        };

        const pendingStep = pendingSourceId
          ? getRecordValue(steps, pendingSourceId)
          : undefined;
        if (pendingSourceId === startNodeId) {
          updated = {
            ...updated,
            initialStepId: newId,
          };
        } else if (pendingSourceId && pendingStep) {
          const prevNext = pendingStep.nextStepIds;
          updated = {
            ...updated,
            steps: {
              ...updatedSteps,
              [pendingSourceId]: {
                ...pendingStep,
                nextStepIds: Array.from(new Set([...prevNext, newId])),
              },
            },
          };
        }

        return updated as T;
      });
      setSelectedStepId(newId);
      closeActionPicker();
    },
    [
      closeActionPicker,
      generateStepId,
      pendingFinal,
      pendingSourceId,
      setDefinitionDraft,
      setSelectedStepId,
      startNodeId,
    ]
  );

  const reachableSteps = React.useMemo(() => {
    const reachable = new Set<string>();
    if (!definitionDraft?.initialStepId) return reachable;
    const steps = definitionDraft.steps ?? {};
    const queue = [definitionDraft.initialStepId];
    while (queue.length) {
      const currentId = queue.shift();
      if (!currentId || reachable.has(currentId)) continue;
      reachable.add(currentId);
      const step = getRecordValue(steps, currentId);
      (step?.nextStepIds ?? []).forEach((nid) => {
        if (!reachable.has(nid)) queue.push(nid);
      });
    }
    if (definitionDraft.finalStep) reachable.add(definitionDraft.finalStep.id);
    return reachable;
  }, [definitionDraft]);

  const filteredActions = React.useMemo(() => {
    const term = actionSearch.trim().toLowerCase();
    if (!term) return actions;
    return actions.filter((a) => {
      const target =
        `${a.name || ''} ${a.description ?? ''} ${a.annotations?.title ?? ''}`.toLowerCase();
      return target.includes(term);
    });
  }, [actionSearch, actions]);

  const hasSteps = React.useMemo(() => {
    const stepCount = Object.keys(definitionDraft?.steps ?? {}).length;
    return stepCount > 0 || Boolean(definitionDraft?.finalStep);
  }, [definitionDraft?.finalStep, definitionDraft?.steps]);

  const hasInitialStep = Boolean(definitionDraft?.initialStepId);

  const handleAddConnection = React.useCallback(
    (sourceId: string, targetId: string) => {
      setDefinitionDraft((current) => {
        if (!current) return current;
        // Promote a step to finalStep when connecting from the end node.
        if (sourceId === startNodeId && targetId === startNodeId) {
          return current;
        }
        if (sourceId === endNodeId) {
          // Do not allow using the initial step as final to avoid breaking the graph.
          if (current.initialStepId === targetId) return current;

          const steps = current.steps ?? {};
          const targetStep = getRecordValue(steps, targetId);
          // If already the final step, nothing to do.
          if (!targetStep && current.finalStep?.id === targetId) {
            return current;
          }
          if (!targetStep) return current;

          const { id, actionId, inputs, description } = targetStep;

          // Remove references to the promoted step from other nextStepIds.
          const cleanedSteps: NonNullable<WorkflowDefinitionDraft['steps']> =
            {};
          Object.entries(steps).forEach(([key, step]) => {
            if (key === targetId) return;
            cleanedSteps[key] = {
              ...step,
              nextStepIds: step.nextStepIds.filter((nid) => nid !== targetId),
            };
          });

          return {
            ...current,
            steps: cleanedSteps,
            finalStep: {
              id,
              actionId,
              inputs,
              description,
            },
          } as T;
        }

        // If the link comes from start, set initial step
        if (sourceId === startNodeId) {
          return {
            ...current,
            initialStepId: targetId,
          } as T;
        }
        const steps = current.steps ?? {};
        const sourceStep = getRecordValue(steps, sourceId);
        if (!sourceStep) return current;
        const nexts = sourceStep.nextStepIds;
        if (nexts.includes(targetId)) return current;

        return {
          ...current,
          steps: {
            ...steps,
            [sourceId]: {
              ...sourceStep,
              nextStepIds: [...nexts, targetId],
            },
          },
        } as T;
      });
    },
    [endNodeId, setDefinitionDraft, startNodeId]
  );

  const handleRemoveConnection = React.useCallback(
    (sourceId: string, targetId: string) => {
      setDefinitionDraft((current) => {
        if (!current) return current;

        if (sourceId === endNodeId) {
          // When removing the final step link, move it back into steps (as a terminal step).
          if (current.finalStep && current.finalStep.id === targetId) {
            const existingSteps = current.steps ?? {};
            const willConflict =
              getRecordValue(existingSteps, targetId) !== undefined;
            const restoredSteps = willConflict
              ? existingSteps
              : {
                  ...existingSteps,
                  [targetId]: {
                    id: current.finalStep.id,
                    actionId: current.finalStep.actionId,
                    inputs: current.finalStep.inputs,
                    nextStepIds: [],
                    description: current.finalStep.description,
                  },
                };
            return {
              ...current,
              steps: restoredSteps,
              finalStep: undefined,
            } as T;
          }
          return current;
        }

        if (sourceId === startNodeId) {
          if (current.initialStepId !== targetId) return current;
          return {
            ...current,
            initialStepId: undefined,
          } as T;
        }

        const steps = current.steps ?? {};
        const sourceStep = getRecordValue(steps, sourceId);
        if (!sourceStep) return current;
        const filteredNext = sourceStep.nextStepIds.filter(
          (id) => id !== targetId
        );
        if (filteredNext.length === sourceStep.nextStepIds.length) {
          return current;
        }

        return {
          ...current,
          steps: {
            ...steps,
            [sourceId]: {
              ...sourceStep,
              nextStepIds: filteredNext,
            },
          },
        } as T;
      });
    },
    [endNodeId, setDefinitionDraft, startNodeId]
  );

  const handleChangeStepInputs = React.useCallback(
    (stepId: string, inputs: Record<string, unknown>) => {
      setDefinitionDraft((current) => {
        if (!current) return current;

        const steps = current.steps ?? {};
        const isFinalStep = current.finalStep?.id === stepId;

        if (isFinalStep) {
          return {
            ...current,
            finalStep: current.finalStep
              ? {
                  ...current.finalStep,
                  inputs,
                }
              : current.finalStep,
          } as T;
        }

        const targetStep = getRecordValue(steps, stepId);
        if (!targetStep) return current;

        return {
          ...current,
          steps: {
            ...steps,
            [stepId]: {
              ...targetStep,
              inputs,
            },
          },
        } as T;
      });
    },
    [setDefinitionDraft]
  );

  const handleDeleteStep = React.useCallback(
    (stepId: string) => {
      setDefinitionDraft((current) => {
        if (!current) return current;
        const steps = current.steps ?? {};
        const nextSteps: NonNullable<WorkflowDefinitionDraft['steps']> = {};
        Object.entries(steps).forEach(([id, step]) => {
          if (id === stepId) return;
          nextSteps[id] = {
            ...step,
            nextStepIds: step.nextStepIds.filter((nid) => nid !== stepId),
          };
        });

        const nextFinal =
          current.finalStep?.id === stepId ? undefined : current.finalStep;
        const nextInitial =
          current.initialStepId === stepId ? undefined : current.initialStepId;

        return {
          ...current,
          steps: nextSteps,
          finalStep: nextFinal,
          initialStepId: nextInitial,
        } as T;
      });
      setSelectedStepId(undefined);
    },
    [setDefinitionDraft, setSelectedStepId]
  );

  return {
    pendingFinal,
    filteredActions,
    reachableSteps,
    hasSteps,
    hasInitialStep,
    handleRequestAddStep,
    handleRequestAddFinal,
    handleSelectAction,
    closeActionPicker,
    handleAddConnection,
    handleRemoveConnection,
    handleChangeStepInputs,
    handleDeleteStep,
  };
};
